import type { PluginListenerHandle } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { StatusBar, Style as StatusBarStyle } from "@capacitor/status-bar";
import PhotoSwipeBase from "photoswipe";

import { safeAreaInsets } from "~/lib/safeareas";
import { isNative } from "~/lib/utils";

import "./lightbox-photoswipe-theme.css";
import "./photoswipe-core.css";

type PointerType = "mouse" | "touch" | "pen";

interface PswpTapEvent extends Event {
	detail: {
		origEvent: MouseEvent | TouchEvent | PointerEvent;
		target: EventTarget;
		releasePoint: {
			type: PointerType;
			x: number;
			y: number;
		};
		pointerType: PointerType;
	};
}

declare global {
	interface GlobalEventHandlersEventMap {
		pswpTap: PswpTapEvent;
	}
}

/** not mentioned in the types, and you can't augment default exports - from `gestures` module */
declare interface PhotoSwipe<T extends PhotoSwipeBase.Options> extends PhotoSwipeBase<T> {
	likelyTouchDevice?: boolean;
	allowProgressiveImg?: () => boolean;
	scrollWrap?: boolean;
	toggleDesktopZoom?: (centerPoint?: { x: number; y: number }) => void;
}

export interface Options extends PhotoSwipeBase.Options {
	/**
	 * Size of top & bottom bars in pixels. "bottom" parameter can be 'auto' (will calculate height of caption).
	 * Option applies only when mouse is used, or when width of screen is more than 1200px.
	 * Also look at `parseVerticalMargin` event.
	 *
	 * Default {top: 44, bottom: "auto"}.
	 */
	barsSize?: { top: number; bottom: number | "auto" };

	/**
	 * Adds class pswp__ui--idle to pswp__ui element when mouse isn't moving for timeToIdle milliseconds.
	 *
	 * Default 4000.
	 */
	timeToIdle?: number;

	/**
	 * Adds class pswp__ui--idle to pswp__ui element when mouse leaves the window for timeToIdleOutside milliseconds.
	 *
	 * Default 1000.
	 */
	timeToIdleOutside?: number;

	/**
	 * Delay in milliseconds until loading indicator is displayed.
	 *
	 * Default 1000.
	 */
	loadingIndicatorDelay?: number;

	/**
	 * Function to build caption markup. The function takes three parameters:
	 *
	 * item      - slide object
	 * captionEl - caption DOM element
	 * isFake    - true when content is added to fake caption container
	 *             (used to get size of next or previous caption)
	 *
	 * Return whether to show the caption or not.
	 *
	 * Default is:
	 *
	 * function(item, captionEl, isFake) {
	 *     if (!item.title) {
	 *         captionEl.children[0].innerHTML = '';
	 *         return false;
	 *     }
	 *     captionEl.children[0].innerHTML = item.title;
	 *     return true;
	 * }
	 *
	 */
	addCaptionHTMLFn?: (item: Item, captionEl: Element, isFake?: boolean) => boolean;

	/**
	 * Tap on sliding area should close gallery.
	 *
	 * Default false.
	 */
	tapToClose?: boolean;

	/**
	 * Tap should toggle visibility of controls.
	 *
	 * Default true.
	 */
	tapToToggleControls?: boolean;

	/**
	 * Mouse click on image should close the gallery, only when image is smaller than size of the viewport.
	 *
	 * Default true.
	 */
	clickToCloseNonZoomable?: boolean;

	/**
	 * Element classes that should close PhotoSwipe when clicked on.
	 * In HTML markup, class should always start with "pswp__", e.g.: "pswp__item", "pswp__caption".
	 *
	 * Default ['item', 'caption', 'zoom-wrap', 'ui', 'top-bar'].
	 */
	closeElClasses?: string[];

	/**
	 * Separator for "1 of X" counter.
	 *
	 * Default ' / '.
	 */
	indexIndicatorSep?: string;

	/**
	 * Unknown
	 *
	 * Default 1200.
	 */
	fitControlsWidth?: number;

	/**
	 * Callback fired when pressing the delete button on a slide
	 * @todo Decide whether it fires with the attachment ID or slide index
	 * @param attachmentId - The GraphQL ID of the attachment being removed
	 * @param index - The slide number being removed, in case the same attachment appears multiple times in the gallery
	 */
	onDelete?: (attachmentId: string, index?: number) => void;
}

/**
 * Extra properties that the Default UI accepts.
 */
export interface Item extends PhotoSwipe.Item {
	/**
	 * The caption for this item.
	 */
	title?: string;
	loading?: boolean;
	img?: HTMLImageElement;
}

// TK: this is redundant, surely?
declare module "photoswipe" {
	interface Item {
		title?: string;
		loading?: boolean;
		img?: HTMLImageElement;
	}
}

const DEFAULT_OPTIONS: Options = {
	barsSize: {
		top: 44,
		bottom: "auto",
	},
	timeToIdle: 4000,
	timeToIdleOutside: 1000,
	loadingIndicatorDelay: 1000,

	addCaptionHTMLFn: function (item, captionEl) {
		if (!item.title) {
			captionEl.children[0].innerHTML = "";
			return false;
		}
		captionEl.children[0].innerHTML = item.title;
		return true;
	},

	tapToClose: false,
	tapToToggleControls: true,

	clickToCloseNonZoomable: true,

	closeElClasses: ["item", "caption", "zoom-wrap", "ui", "top-bar"],

	indexIndicatorSep: " of ",

	fitControlsWidth: 1200,
};

export default class LightboxPhotoSwipeUI implements PhotoSwipeBase.UI<Options> {
	pswp: PhotoSwipe<Options>;
	framework: PhotoSwipe.UIFramework;
	options: Required<Options>;

	overlayUIUpdated = false;
	controlsVisible = true;
	controls: Element;
	captionContainer: Element;
	fakeCaptionContainer?: Element;
	indexIndicator: Element;
	isIdle = false;
	listen: PhotoSwipe<Options>["listen"];

	/** Capacitor listeners use a weird handle system instead of eventListeners, so we need to store this to remove it */
	capacitorKeyboardListener?: PluginListenerHandle;

	loadingIndicator: Element;
	loadingIndicatorHidden = true;
	loadingIndicatorTimeout?: number;

	galleryHasOneSlide = false;

	blockControlsTap = false;
	blockControlsTapTimeout?: number;

	controlsActions: { name: string; onTap: () => void }[];

	constructor(pswp: PhotoSwipe<Options>, framework: PhotoSwipe.UIFramework) {
		this.pswp = pswp;
		this.listen = pswp.listen;
		this.framework = framework;

		// all the following was originally in init, but "should" be in constructor I think?
		// hopefully it doesn't break
		// In PhotoSwipe's internals, `init` is called immediately after constructing the instance, so we should be fine

		// extend options
		this.framework.extend(pswp.options, DEFAULT_OPTIONS, true);

		// create local link for fast access
		this.options = this.pswp.options as Required<Options>; // types have this as Partial for convenience, wrongly

		// find pswp__ui element
		this.controls = this.framework.getChildByClass(pswp.scrollWrap, "pswp__ui");
		if (!this.controls) console.error("PhotoSwipe controls element not found");

		this.controlsActions = [
			{
				name: "button--close",
				onTap: this.pswp.close,
			},
			{
				name: "button--arrow--left",
				onTap: this.pswp.prev,
			},
			{
				name: "button--arrow--right",
				onTap: this.pswp.next,
			},
			{
				name: "button--trash",
				onTap: this.onTrashTap,
			},
			{
				name: "button--download",
				onTap: this.onDownloadTap,
			},
		];

		this.captionContainer = this.controls.getElementsByClassName(`pswp__caption`)[0];
		if (!this.captionContainer) console.error("PhotoSwipe captionContainer element not found");

		this.indexIndicator = this.controls.getElementsByClassName(`pswp__counter`)[0];
		if (!this.indexIndicator) console.error("PhotoSwipe indexIndicator element not found");

		this.loadingIndicator = this.controls.getElementsByClassName(`pswp__preloader`)[0];
		if (!this.loadingIndicator) console.error("PhotoSwipe loadingIndicator element not found");
	}

	onControlsTap = (e: PswpTapEvent) => {
		if (this.blockControlsTap) return;

		if (this.options.timeToIdle && this.options.mouseUsed && !this.isIdle) {
			// reset idle timer
			this.onIdleMouseMove();
		}

		let { target } = e,
			clickedClasses = target instanceof Element ? target.classList : null,
			found = false;

		for (let controlsAction of this.controlsActions) {
			if (controlsAction.onTap && clickedClasses?.contains(`pswp__${controlsAction.name}`)) {
				controlsAction.onTap();
				found = true;
			}
		}

		if (found) {
			e.stopPropagation();
		}

		this.blockControlsTap = true;

		// Some versions of Android don't prevent ghost click event
		// when preventDefault() was called on touchstart and/or touchend.
		//
		// This happens on v4.3, 4.2, 4.1,
		// older versions strangely work correctly,
		// but just in case we add delay on all of them)
		let tapDelay = this.framework.features.isOldAndroid ? 600 : 30;
		this.blockControlsTapTimeout = window.setTimeout(() => {
			this.blockControlsTap = false;
		}, tapDelay);
	};

	onDownloadTap = () => {
		const src = this.pswp.currItem.src;
		if (src) {
			const link = document.createElement("a");
			link.href = src;
			link.target = "_blank";
			link.rel = "noopener";
			link.download = "";
			link.style.setProperty("display", "none");
			fetch(src)
				.then(res => {
					return res.blob();
				})
				.then(blob => {
					const url = URL.createObjectURL(blob);
					const pathSegments = new URL(src).pathname.split("/");
					const fileName = pathSegments[pathSegments.length - 1];
					link.href = url;
					if (fileName) {
						link.download = fileName;
					}
				})
				.finally(() => {
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				});
		}
	};

	onTrashTap = () => {
		const attachmentId = this.pswp.currItem.attachmentId;

		if (attachmentId !== undefined) {
			this.options.onDelete?.(attachmentId, this.pswp.getCurrentIndex());
		}
	};

	fitControlsInViewport = () => {
		// TODO: not sure how likelyTouchDevice & mouseUsed work, but something newer like MatchMedia might be preferable
		return !this.pswp.likelyTouchDevice || this.options.mouseUsed || screen.width > this.options.fitControlsWidth;
	};

	togglePswpClass = (el: Element, cName: string, add: boolean) => {
		el.classList.toggle(`pswp__${cName}`, add);
	};

	countNumItems = () => {
		let hasOneSlide = this.options.getNumItemsFn?.() === 1;

		if (hasOneSlide !== this.galleryHasOneSlide) {
			this.togglePswpClass(this.controls, "ui--one-slide", hasOneSlide);
			this.galleryHasOneSlide = hasOneSlide;
		}
	};

	hasCloseClass = (target: Element) => {
		return this.options.closeElClasses.some(c => target.classList.contains(`pswp__${c}`));
	};

	idleInterval?: number;
	idleTimer?: number;
	idleIncrement = 0;
	onIdleMouseMove = () => {
		window.clearTimeout(this.idleTimer);
		this.idleIncrement = 0;
		if (this.isIdle) {
			this.setIdle(false);
		}
	};
	onMouseLeaveWindow = (e: MouseEvent) => {
		const from = e.relatedTarget;
		if (!from || (from instanceof Node && from.nodeName === "HTML")) {
			window.clearTimeout(this.idleTimer);
			this.idleTimer = window.setTimeout(() => {
				this.setIdle(true);
			}, this.options.timeToIdleOutside);
		}
	};

	setupIdle = () => {
		// Hide controls when mouse is used
		if (this.options.timeToIdle) {
			this.listen("mouseUsed", () => {
				document.addEventListener("mousemove", this.onIdleMouseMove);
				document.addEventListener("mouseout", this.onMouseLeaveWindow);

				this.idleInterval = window.setInterval(() => {
					this.idleIncrement++;
					if (this.idleIncrement === 2) {
						this.setIdle(true);
					}
				}, this.options.timeToIdle / 2);
			});
		}
	};

	setIdle = (isIdle: boolean) => {
		this.isIdle = isIdle;
		this.togglePswpClass(this.controls, "ui--idle", isIdle);
	};

	setupLoadingIndicator = () => {
		// Setup loading indicator
		this.toggleLoadingIndicator(true);

		this.listen("beforeChange", () => {
			window.clearTimeout(this.loadingIndicatorTimeout);

			// display loading indicator with delay
			this.loadingIndicatorTimeout = window.setTimeout(() => {
				if (this.pswp.currItem && this.pswp.currItem.loading) {
					if (!this.pswp.allowProgressiveImg?.() || (this.pswp.currItem.img && !this.pswp.currItem.img.naturalWidth)) {
						// show preloader if progressive loading is not enabled,
						// or image width is not defined yet (because of slow connection)
						this.toggleLoadingIndicator(false);
						// items-controller.js function allowProgressiveImg
					}
				} else {
					this.toggleLoadingIndicator(true); // hide preloader
				}
			}, this.options.loadingIndicatorDelay);
		});

		this.listen("imageLoadComplete", (index, item) => {
			if (this.pswp.currItem === item) {
				this.toggleLoadingIndicator(true);
			}
		});
	};
	toggleLoadingIndicator = (hide: boolean) => {
		if (this.loadingIndicatorHidden !== hide) {
			this.togglePswpClass(this.loadingIndicator, "preloader--active", !hide);
			this.loadingIndicatorHidden = hide;
		}
	};

	applyNavBarGaps = (item: Item) => {
		if (item.vGap === undefined) item.vGap = { top: 0, bottom: 0 };
		let gap = item.vGap;

		if (this.fitControlsInViewport()) {
			let bars = this.options.barsSize;
			if (bars.bottom === "auto") {
				if (!this.fakeCaptionContainer) {
					const fakeCaptionContainer: HTMLElement = this.framework.createEl("pswp__caption pswp__caption--fake");
					this.fakeCaptionContainer = fakeCaptionContainer;
					this.fakeCaptionContainer.appendChild(this.framework.createEl("pswp__caption__center"));
					this.controls.insertBefore(this.fakeCaptionContainer, this.captionContainer);
					this.framework.addClass(this.controls, "pswp__ui--fit");
				}

				if (this.options.addCaptionHTMLFn(item, this.fakeCaptionContainer, true)) {
					gap.bottom = this.fakeCaptionContainer.clientHeight || 44;
				} else {
					gap.bottom = bars.top; // if no caption, set size of bottom gap to size of top
				}
			} else {
				gap.bottom = bars.bottom;
			}

			// height of top bar is static, no need to calculate it
			gap.top = bars.top;

			gap.top += safeAreaInsets.top;
			gap.bottom += safeAreaInsets.bottom;
		} else {
			gap.top = safeAreaInsets.top;
			gap.bottom = safeAreaInsets.bottom;
		}
	};

	setupHidingControlsDuringGestures = () => {
		// Hide controls on vertical drag
		this.listen("onVerticalDrag", now => {
			if (this.controlsVisible && now < 0.95) {
				this.hideControls();
			} else if (!this.controlsVisible && now >= 0.95) {
				this.showControls();
			}
		});

		// Hide controls when pinching to close
		let pinchControlsHidden = false;
		this.listen("onPinchClose", now => {
			if (this.controlsVisible && now < 0.9) {
				this.hideControls();
				pinchControlsHidden = true;
			} else if (pinchControlsHidden && !this.controlsVisible && now > 0.9) {
				this.showControls();
			}
		});

		this.listen("zoomGestureEnded", () => {
			pinchControlsHidden = false;
			if (pinchControlsHidden && !this.controlsVisible) {
				this.showControls();
			}
		});
	};

	init = () => {
		const { pswp, framework } = this;

		// create local link
		this.listen = pswp.listen;

		this.setupHidingControlsDuringGestures();

		// update controls when slides change
		this.listen("beforeChange", this.update);

		// toggle zoom on double-tap
		this.listen("doubleTap", point => {
			let initialZoomLevel = pswp.currItem.initialZoomLevel;
			if (initialZoomLevel !== undefined && pswp.getZoomLevel() !== initialZoomLevel) {
				pswp.zoomTo(initialZoomLevel, point, 333);
			} else {
				pswp.zoomTo(this.options.getDoubleTapZoom?.(false, pswp.currItem), point, 333);
			}
		});

		// Allow text selection in caption
		this.listen("preventDragEvent", function (e, isDown, preventObj) {
			let t = e.target;
			if (
				t instanceof HTMLElement &&
				t.getAttribute("class") &&
				e.type.includes("mouse") &&
				(t.className.includes("__caption") || /(small|strong|em)/i.test(t.tagName))
			) {
				preventObj.prevent = false;
			}
		});

		// bind events for UI
		this.listen("bindEvents", () => {
			framework.bind(this.controls, "pswpTap click", this.onControlsTap);
			framework.bind(pswp.scrollWrap, "pswpTap", this.onGlobalTap);

			if (!pswp.likelyTouchDevice) {
				framework.bind(pswp.scrollWrap, "mouseover", this.onMouseOver);
			}

			window.addEventListener("-fellow-safeareachange", this.updateSize);
			if (isNative) {
				// Sometimes, on iOS at least, the image stays top-aligned as though the keyboard was still up
				this.capacitorKeyboardListener = Keyboard.addListener("keyboardDidHide", this.updateSize);
			}
		});

		// unbind events for UI
		this.listen("unbindEvents", () => {
			if (this.idleInterval) {
				window.clearInterval(this.idleInterval);
			}
			framework.unbind(document, "mouseout", this.onMouseLeaveWindow);
			framework.unbind(document, "mousemove", this.onIdleMouseMove);
			framework.unbind(this.controls, "pswpTap click", this.onControlsTap);
			framework.unbind(pswp.scrollWrap, "pswpTap", this.onGlobalTap);
			framework.unbind(pswp.scrollWrap, "mouseover", this.onMouseOver);

			window.removeEventListener("-fellow-safeareachange", this.updateSize);
			if (isNative) {
				this.capacitorKeyboardListener?.remove();
			}
		});

		// clean up things when gallery is destroyed
		this.listen("destroy", () => {
			if (this.fakeCaptionContainer) {
				this.controls.removeChild(this.fakeCaptionContainer);
			}
			framework.removeClass(this.captionContainer, "pswp__caption--empty");

			framework.addClass(this.controls, "pswp__ui--hidden");
			this.setIdle(false);

			if (window.Capacitor.platform === "ios") {
				StatusBar.setStyle({ style: StatusBarStyle.Light });
			}
		});

		if (window.Capacitor.platform === "ios") {
			StatusBar.setStyle({ style: StatusBarStyle.Dark });
		}

		if (!this.options.showAnimationDuration) {
			framework.removeClass(this.controls, "pswp__ui--hidden");
		}
		this.listen("initialZoomIn", () => {
			if (this.options.showAnimationDuration) {
				framework.removeClass(this.controls, "pswp__ui--hidden");
			}
		});
		this.listen("initialZoomOut", () => {
			framework.addClass(this.controls, "pswp__ui--hidden");
		});

		this.listen("parseVerticalMargin", this.applyNavBarGaps);

		this.countNumItems();

		this.setupIdle();

		this.setupLoadingIndicator();
	};

	update = () => {
		// Don't update UI if it's hidden
		if (this.controlsVisible && this.pswp.currItem) {
			this.updateIndexIndicator();

			this.options.addCaptionHTMLFn(this.pswp.currItem, this.captionContainer);
			this.togglePswpClass(this.captionContainer, "caption--empty", !this.pswp.currItem.caption);

			this.overlayUIUpdated = true;
		} else {
			this.overlayUIUpdated = false;
		}

		this.countNumItems();
	};

	updateIndexIndicator = () => {
		this.indexIndicator.innerHTML =
			this.pswp.getCurrentIndex() + 1 + this.options.indexIndicatorSep + (this.options.getNumItemsFn?.() ?? "0");
	};

	onGlobalTap = (e: PswpTapEvent) => {
		const { target } = e;

		if (!(target instanceof Element)) return;
		if (this.blockControlsTap) return;

		if (e.detail && e.detail.pointerType === "mouse") {
			// close gallery if clicked outside of the image
			if (this.hasCloseClass(target)) {
				this.pswp.close();
				return;
			}

			if (target.classList.contains("pswp__img")) {
				if (this.pswp.getZoomLevel() === 1 && this.pswp.getZoomLevel() <= (this.pswp.currItem.fitRatio ?? 1)) {
					if (this.options.clickToCloseNonZoomable) {
						this.pswp.close();
					}
				} else {
					this.pswp.toggleDesktopZoom?.(e.detail.releasePoint);
				}
			}
		} else {
			// tap anywhere (except buttons) to toggle visibility of controls
			if (this.options.tapToToggleControls) {
				if (this.controlsVisible) {
					this.hideControls();
				} else {
					this.showControls();
				}
			}

			// tap to close gallery
			if (this.options.tapToClose && (target.classList.contains("pswp__img") || this.hasCloseClass(target))) {
				this.pswp.close();
				return;
			}
		}
	};

	onMouseOver = (e: MouseEvent) => {
		const { target } = e;

		if (!(target instanceof Element)) return;
	};

	updateSize = () => {
		this.pswp.items.forEach(i => this.applyNavBarGaps(i));
		this.pswp.updateSize(true);
	};

	hideControls = () => {
		this.controls.classList.add("pswp__ui--hidden");
		this.controlsVisible = false;
	};

	showControls = () => {
		this.controlsVisible = true;
		if (!this.overlayUIUpdated) {
			this.update();
		}
		this.controls.classList.remove("pswp__ui--hidden");
	};
}
